import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function TicketDetail() {
  const { id } = useParams();
  const [ticket, setTicket] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/tickets/${id}`).then((r) => setTicket(r.data));
  useEffect(() => { load(); }, [id]);

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/tickets/${id}/messages`, { message: msg });
      setMsg(""); load();
    } catch { toast.error("Send failed"); }
    setBusy(false);
  };
  const close = async () => {
    if (!window.confirm("Close this ticket?")) return;
    await api.post(`/tickets/${id}/close`); toast.success("Ticket closed"); load();
  };

  if (!ticket) return <div className="text-slate-400 font-mono text-xs uppercase tracking-widest">Loading…</div>;

  return (
    <div data-testid="ticket-detail">
      <Link to="/dashboard/tickets" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft className="w-4 h-4" strokeWidth={1.75}/> Back to tickets</Link>
      <div className="mt-4 border border-slate-200 rounded-md bg-white p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono text-xs text-slate-400">{ticket.ticket_id}</div>
            <h1 className="mt-1 font-display text-2xl font-semibold text-slate-900">{ticket.subject}</h1>
            <div className="text-xs text-slate-500 mt-1">{ticket.category} · {ticket.priority}</div>
          </div>
          <Badge className="rounded-md font-mono text-[10px] uppercase tracking-widest border border-slate-200 bg-slate-50 text-slate-700">{ticket.status}</Badge>
        </div>
      </div>

      <div className="mt-4 space-y-3">
        {ticket.messages.map((m) => (
          <div key={m.message_id} className={`border rounded-md p-4 ${m.author === "admin" ? "border-blue-200 bg-blue-50/40" : "border-slate-200 bg-white"}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium text-slate-900">{m.author_name} <span className="text-xs text-slate-500">· {m.author === "admin" ? "Support" : "You"}</span></div>
              <div className="text-xs text-slate-500">{new Date(m.at).toLocaleString()}</div>
            </div>
            <div className="text-sm text-slate-700 whitespace-pre-wrap">{m.body}</div>
          </div>
        ))}
      </div>

      {ticket.status !== "Closed" ? (
        <form onSubmit={send} className="mt-6 border border-slate-200 rounded-md bg-white p-4 space-y-2">
          <Textarea rows={4} placeholder="Reply…" value={msg} onChange={(e) => setMsg(e.target.value)} required data-testid="ticket-reply-input"/>
          <div className="flex justify-between">
            <Button type="button" variant="ghost" onClick={close}>Close ticket</Button>
            <Button type="submit" disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="ticket-reply-send">Send reply</Button>
          </div>
        </form>
      ) : (
        <div className="mt-6 text-center text-sm text-slate-500 border border-dashed border-slate-200 rounded-md p-6">This ticket is closed.</div>
      )}
    </div>
  );
}
